import React, { useState, useEffect } from 'react';
import DateInput from '../Common/DateInput';
import { planes, planeModels, planeRegs, planeModelsHistory } from '../../services/api';
import { formatDate, formatPartialDate } from '../../utils/dateFormat';
import { aircraftManufacturers } from '../../constants/aircraftManufacturers';

const statuses = { 0: 'Retired', 1: 'Active' };

function PlaneDetails({ plane, onClose, onUpdate }) {
  const [regs, setRegs] = useState([]);
  const [models, setModels] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegForm, setShowRegForm] = useState(false);
  const [showModelForm, setShowModelForm] = useState(false);
  const [editingReg, setEditingReg] = useState(null);
  const [editingModel, setEditingModel] = useState(null);
  const [regFormData, setRegFormData] = useState({ preg_plane_id: plane.plane_id, preg_value: '', preg_start_date: '', preg_end_date: '' });
  const [modelFormData, setModelFormData] = useState({ pmh_plane_id: plane.plane_id, pmh_pmodel_id: '', pmh_start_date: '', pmh_end_date: '' });

  useEffect(() => {
    loadData();
  }, [plane.plane_id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [regsRes, modelsRes, allModelsRes] = await Promise.all([
        planeRegs.getByPlane(plane.plane_id),
        planeModelsHistory.getByPlane(plane.plane_id),
        planeModels.getAll()
      ]);
      setRegs(regsRes.data);
      setModels(modelsRes.data);
      setAllModels(allModelsRes.data);
    } catch (err) {
      console.error('Failed to load plane details', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReg = async () => {
    try {
      if (editingReg) {
        await planeRegs.update(editingReg, regFormData);
      } else {
        await planeRegs.create(regFormData);
      }
      setShowRegForm(false);
      setEditingReg(null);
      setRegFormData({ preg_plane_id: plane.plane_id, preg_value: '', preg_start_date: '', preg_end_date: '' });
      loadData();
      onUpdate && onUpdate();
    } catch (err) {
      alert('Failed to save registration');
    }
  };

  const handleEditReg = (reg) => {
    setRegFormData({
      preg_plane_id: plane.plane_id,
      preg_value: reg.preg_value,
      preg_start_date: reg.preg_start_date?.split('T')[0] || '',
      preg_end_date: reg.preg_end_date?.split('T')[0] || ''
    });
    setEditingReg(reg.preg_id);
    setShowRegForm(true);
  };

  const handleDeleteReg = async (id) => {
    if (window.confirm('Delete this registration?')) {
      try {
        await planeRegs.delete(id);
        loadData();
        onUpdate && onUpdate();
      } catch (err) {
        alert('Failed to delete registration');
      }
    }
  };

  const handleAddModel = async () => {
    try {
      if (editingModel) {
        await planeModelsHistory.update(editingModel, modelFormData);
      } else {
        await planeModelsHistory.create(modelFormData);
      }
      setShowModelForm(false);
      setEditingModel(null);
      setModelFormData({ pmh_plane_id: plane.plane_id, pmh_pmodel_id: '', pmh_start_date: '', pmh_end_date: '' });
      loadData();
      onUpdate && onUpdate();
    } catch (err) {
      alert('Failed to save model history');
    }
  };

  const handleEditModel = (m) => {
    setModelFormData({
      pmh_plane_id: plane.plane_id,
      pmh_pmodel_id: m.pmh_pmodel_id,
      pmh_start_date: m.pmh_start_date?.split('T')[0] || '',
      pmh_end_date: m.pmh_end_date?.split('T')[0] || ''
    });
    setEditingModel(m.pmh_id);
    setShowModelForm(true);
  };

  const handleDeleteModel = async (id) => {
    if (window.confirm('Delete this model history record?')) {
      try {
        await planeModelsHistory.delete(id);
        loadData();
        onUpdate && onUpdate();
      } catch (err) {
        alert('Failed to delete model history');
      }
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', padding: '20px', borderRadius: '8px', maxWidth: '800px', width: '90%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>Plane Details: MSN {plane.plane_msn}</h3>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          {/* Registrations */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4>Registrations</h4>
              <button className="btn btn-primary" onClick={() => { setEditingReg(null); setRegFormData({ preg_plane_id: plane.plane_id, preg_value: '', preg_start_date: '', preg_end_date: '' }); setShowRegForm(true); }}>Add</button>
            </div>
            {showRegForm && (
              <div style={{ background: '#f8f9fa', padding: '10px', marginBottom: '10px', borderRadius: '4px' }}>
                <div className="form-group">
                  <label>Registration *</label>
                  <input type="text" value={regFormData.preg_value} onChange={(e) => setRegFormData({ ...regFormData, preg_value: e.target.value })} maxLength={32} />
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <DateInput value={regFormData.preg_start_date} onChange={(v) => setRegFormData({ ...regFormData, preg_start_date: v })} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <DateInput value={regFormData.preg_end_date} onChange={(v) => setRegFormData({ ...regFormData, preg_end_date: v })} />
                </div>
                <button className="btn btn-success" onClick={handleAddReg}>{editingReg ? 'Update' : 'Save'}</button>
                <button className="btn btn-secondary" onClick={() => { setShowRegForm(false); setEditingReg(null); }} style={{ marginLeft: '10px' }}>Cancel</button>
              </div>
            )}
            <table className="table">
              <thead>
                <tr>
                  <th>Registration</th>
                  <th>From</th>
                  <th>To</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {regs.map(reg => (
                  <tr key={reg.preg_id}>
                    <td><strong>{reg.preg_value}</strong></td>
                    <td>{formatDate(reg.preg_start_date) || '-'}</td>
                    <td>{formatDate(reg.preg_end_date) || '-'}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleEditReg(reg)} style={{ marginRight: '5px' }}>✎</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteReg(reg.preg_id)}>×</button>
                    </td>
                  </tr>
                ))}
                {regs.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#7f8c8d' }}>No registrations</td></tr>}
              </tbody>
            </table>
          </div>

          {/* Model History */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <h4>Model History</h4>
              <button className="btn btn-primary" onClick={() => { setEditingModel(null); setModelFormData({ pmh_plane_id: plane.plane_id, pmh_pmodel_id: '', pmh_start_date: '', pmh_end_date: '' }); setShowModelForm(true); }}>Add</button>
            </div>
            {showModelForm && (
              <div style={{ background: '#f8f9fa', padding: '10px', marginBottom: '10px', borderRadius: '4px' }}>
                <div className="form-group">
                  <label>Model *</label>
                  <select value={modelFormData.pmh_pmodel_id} onChange={(e) => setModelFormData({ ...modelFormData, pmh_pmodel_id: parseInt(e.target.value) })}>
                    <option value="">Select model</option>
                      {allModels.map(m => (
                        <option key={m.pmodel_id} value={m.pmodel_id}>{aircraftManufacturers[m.pmodel_manufacturer]} {m.pmodel_designation}</option>
                      ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Start Date</label>
                  <DateInput value={modelFormData.pmh_start_date} onChange={(v) => setModelFormData({ ...modelFormData, pmh_start_date: v })} />
                </div>
                <div className="form-group">
                  <label>End Date</label>
                  <DateInput value={modelFormData.pmh_end_date} onChange={(v) => setModelFormData({ ...modelFormData, pmh_end_date: v })} />
                </div>
                <button className="btn btn-success" onClick={handleAddModel}>{editingModel ? 'Update' : 'Save'}</button>
                <button className="btn btn-secondary" onClick={() => { setShowModelForm(false); setEditingModel(null); }} style={{ marginLeft: '10px' }}>Cancel</button>
              </div>
            )}
            <table className="table">
              <thead>
                <tr>
                  <th>Model</th>
                  <th>From</th>
                  <th>To</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {models.map(m => (
                  <tr key={m.pmh_id}>
                    <td><strong>{aircraftManufacturers[m.pmodel_manufacturer]} {m.pmodel_designation}</strong></td>
                    <td>{formatDate(m.pmh_start_date) || '-'}</td>
                    <td>{formatDate(m.pmh_end_date) || '-'}</td>
                    <td>
                      <button className="btn btn-secondary btn-sm" onClick={() => handleEditModel(m)} style={{ marginRight: '5px' }}>✎</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteModel(m.pmh_id)}>×</button>
                    </td>
                  </tr>
                ))}
                {models.length === 0 && <tr><td colSpan={4} style={{ textAlign: 'center', color: '#7f8c8d' }}>No model history</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function PlanesList() {
  const [items, setItems] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedPlane, setSelectedPlane] = useState(null);
  const [formData, setFormData] = useState({
    plane_msn: '',
    plane_status: 1,
    plane_mfg_year: '',
    plane_mfg_month: '',
    plane_mfg_day: '',
    plane_scrapped_date: '',
    // Extended fields for initial reg/model
    initial_reg: '',
    initial_model_id: ''
  });

  const toIntOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = parseInt(value, 10);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const buildFullDateOrNull = (year, month, day) => {
    const y = toIntOrNull(year);
    const m = toIntOrNull(month);
    const d = toIntOrNull(day);
    if (!y || !m || !d) return null;
    const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
    return `${y}-${pad(m)}-${pad(d)}`;
  };

  useEffect(() => {
    loadItems();
    loadModels();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const response = await planes.getAll();
      setItems(response.data);
    } catch (err) {
      setError('Failed to load planes');
    } finally {
      setLoading(false);
    }
  };

  const loadModels = async () => {
    try {
      const response = await planeModels.getAll();
      setAllModels(response.data);
    } catch (err) {
      console.error('Failed to load models', err);
    }
  };

  const handleCreate = () => {
    setFormData({
      plane_msn: '',
      plane_status: 1,
      plane_mfg_year: '',
      plane_mfg_month: '',
      plane_mfg_day: '',
      plane_scrapped_date: '',
      initial_reg: '',
      initial_model_id: ''
    });
    setEditingId(null);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setFormData({
      plane_msn: item.plane_msn || '',
      plane_status: item.plane_status ?? 1,
      plane_mfg_year: item.plane_mfg_year ?? '',
      plane_mfg_month: item.plane_mfg_month ?? '',
      plane_mfg_day: item.plane_mfg_day ?? '',
      plane_scrapped_date: item.plane_scrapped_date?.split('T')[0] || '',
      initial_reg: '',
      initial_model_id: ''
    });
    setEditingId(item.plane_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this plane?')) return;
    try {
      await planes.delete(id);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete plane');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const manufactureExactDate = buildFullDateOrNull(formData.plane_mfg_year, formData.plane_mfg_month, formData.plane_mfg_day);

      const planeData = {
        plane_msn: formData.plane_msn,
        plane_status: formData.plane_status,
        plane_mfg_year: toIntOrNull(formData.plane_mfg_year),
        plane_mfg_month: toIntOrNull(formData.plane_mfg_month),
        plane_mfg_day: toIntOrNull(formData.plane_mfg_day),
        plane_scrapped_date: formData.plane_scrapped_date || null
      };

      let planeId = editingId;

      if (editingId) {
        await planes.update(editingId, planeData);
      } else {
        const result = await planes.create(planeData);
        planeId = result.data.plane_id;

        // Create initial registration if provided
        if (formData.initial_reg) {
          await planeRegs.create({
            preg_plane_id: planeId,
            preg_value: formData.initial_reg,
            preg_start_date: manufactureExactDate,
            preg_end_date: null
          });
        }

        // Create initial model history if provided
        if (formData.initial_model_id) {
          await planeModelsHistory.create({
            pmh_plane_id: planeId,
            pmh_pmodel_id: formData.initial_model_id,
            pmh_start_date: manufactureExactDate,
            pmh_end_date: null
          });
        }
      }

      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save plane');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setError('');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (showForm) {
    return (
      <div>
        <div className="page-header">
          <h2>{editingId ? 'Edit Plane' : 'Create Plane'}</h2>
        </div>
        {error && <div className="alert alert-danger">{error}</div>}
        <form onSubmit={handleSubmit} className="form">
          <div className="form-group">
            <label>MSN (Manufacturer Serial Number) *</label>
            <input
              type="text"
              value={formData.plane_msn || ''}
              onChange={(e) => setFormData({ ...formData, plane_msn: e.target.value })}
              required
              maxLength={32}
            />
          </div>

          <div className="form-group">
            <label>Status *</label>
            <select
              value={formData.plane_status ?? 1}
              onChange={(e) => setFormData({ ...formData, plane_status: parseInt(e.target.value) })}
              required
            >
              <option value={1}>Active</option>
              <option value={0}>Retired</option>
            </select>
          </div>

          <div className="form-group">
            <label>Manufacture (year / month / day)</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="number"
                placeholder="YYYY"
                value={formData.plane_mfg_year}
                onChange={(e) => setFormData({ ...formData, plane_mfg_year: e.target.value })}
                min="1900"
                max="2100"
                style={{ width: '90px' }}
              />
              <input
                type="number"
                placeholder="MM"
                value={formData.plane_mfg_month}
                onChange={(e) => setFormData({ ...formData, plane_mfg_month: e.target.value })}
                min="1"
                max="12"
                style={{ width: '70px' }}
              />
              <input
                type="number"
                placeholder="DD"
                value={formData.plane_mfg_day}
                onChange={(e) => setFormData({ ...formData, plane_mfg_day: e.target.value })}
                min="1"
                max="31"
                style={{ width: '70px' }}
              />
            </div>
            <small style={{ color: '#7f8c8d' }}>Fill what is known; leave unknown parts empty.</small>
          </div>

          <div className="form-group">
            <label>Scrapped Date</label>
            <DateInput
              value={formData.plane_scrapped_date || ''}
              onChange={(value) => setFormData({ ...formData, plane_scrapped_date: value })}
            />
          </div>

          {!editingId && (
            <>
              <hr style={{ margin: '20px 0', borderColor: '#ecf0f1' }} />
              <h4 style={{ marginBottom: '15px', color: '#7f8c8d' }}>Initial Configuration (optional)</h4>
              
              <div className="form-group">
                <label>Initial Registration</label>
                <input
                  type="text"
                  value={formData.initial_reg || ''}
                  onChange={(e) => setFormData({ ...formData, initial_reg: e.target.value })}
                  maxLength={32}
                  placeholder="e.g., VP-BKI"
                />
                <small style={{ color: '#7f8c8d' }}>Will use manufacture date as start date</small>
              </div>

              <div className="form-group">
                <label>Initial Model</label>
                <select
                  value={formData.initial_model_id || ''}
                  onChange={(e) => setFormData({ ...formData, initial_model_id: e.target.value ? parseInt(e.target.value) : '' })}
                >
                  <option value="">Select model (optional)</option>
                  {allModels.map(m => (
                    <option key={m.pmodel_id} value={m.pmodel_id}>
                      {aircraftManufacturers[m.pmodel_manufacturer]} {m.pmodel_designation}
                    </option>
                  ))}
                </select>
                <small style={{ color: '#7f8c8d' }}>Will use manufacture date as start date</small>
              </div>
            </>
          )}

          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {editingId ? 'Update' : 'Create'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Planes</h2>
        <button className="btn btn-primary" onClick={handleCreate}>Add New</button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      <table className="table">
        <thead>
          <tr>
            <th>ID</th>
            <th>MSN</th>
            <th>Current Registration</th>
            <th>Current Model</th>
            <th>Status</th>
            <th>Manufacture Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.plane_id}>
              <td>{item.plane_id}</td>
              <td>
                <button
                  className="btn-link"
                  onClick={() => setSelectedPlane(item)}
                  style={{ background: 'none', border: 'none', color: '#3498db', cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {item.plane_msn}
                </button>
              </td>
              <td>{item.current_reg || '-'}</td>
              <td>{item.current_model ? `${aircraftManufacturers[item.pmodel_manufacturer] || ''} ${item.current_model}` : '-'}</td>
              <td>
                <span className={`badge ${item.plane_status === 1 ? 'badge-success' : 'badge-secondary'}`}>
                  {statuses[item.plane_status]}
                </span>
              </td>
              <td>{formatPartialDate(item.plane_mfg_year, item.plane_mfg_month, item.plane_mfg_day) || '-'}</td>
              <td>
                <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(item)}>Edit</button>
                <button className="btn btn-danger btn-sm" onClick={() => handleDelete(item.plane_id)} style={{ marginLeft: '5px' }}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {selectedPlane && (
        <PlaneDetails 
          plane={selectedPlane} 
          onClose={() => setSelectedPlane(null)} 
          onUpdate={loadItems}
        />
      )}
    </div>
  );
}

export default PlanesList;
